import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowUpRight, ArrowRight } from 'lucide-react';
import { useLang, ui } from '../i18n/LanguageContext';
import { santePhysiqueContent as content, team } from '../mock/data';
import { PageHero } from './About';
import { PersonPhoto } from '../components/PersonPhoto';
import useReveal from '../hooks/useReveal';

const TABS = ['physiotherapie', 'osteopathie'];

const SantePhysique = () => {
  const { t } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const active = TABS.includes(tabParam) ? tabParam : 'physiotherapie';
  useReveal();

  const data = content[active];
  const practitioner = data.practitionerSlug ? team.find((m) => m.slug === data.practitionerSlug) : null;

  const setTab = (id) => setSearchParams({ tab: id });

  return (
    <div data-testid="sante-physique-page">
      <PageHero tag={t(content.hero.tag)} title={t(content.hero.title)} image={content.hero.image} />

      <section className="py-16 md:py-24">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <p className="text-lg text-muted-foreground max-w-3xl mb-12 leading-relaxed reveal">{t(content.intro)}</p>

          {/* Tabs */}
          <div className="flex gap-0 border-b border-border mb-12" data-testid="sante-physique-tabs">
            {TABS.map((id) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                data-testid={`tab-${id}`}
                className={`relative px-6 md:px-8 py-4 text-sm md:text-base tracking-wide transition-colors duration-300 ${
                  active === id ? 'text-[#161514]' : 'text-muted-foreground hover:text-[#161514]'
                }`}
              >
                {t(content[id].label)}
                <span
                  className={`absolute left-0 -bottom-px h-0.5 bg-champagne transition-all duration-300 ${
                    active === id ? 'w-full' : 'w-0'
                  }`}
                />
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div key={active} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start animate-cookie-in">
            <div className="lg:col-span-5">
              <div className="img-zoom aspect-[4/5] bg-secondary">
                <img src={data.image} alt={t(data.label)} className="w-full h-full object-cover grayscale" />
              </div>
            </div>

            <div className="lg:col-span-7">
              <h2 className="font-serif text-3xl md:text-4xl text-[#161514] mb-8">{t(data.label)}</h2>
              <div className="space-y-6 text-lg text-[#3a3833] leading-relaxed">
                {t(data.paragraphs).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>

              {practitioner && (
                <Link
                  to={`/equipe/${practitioner.slug}`}
                  className="group inline-flex items-center gap-4 mt-10 bg-secondary p-5 pr-7 hover:bg-[#161514] transition-colors duration-300"
                  data-testid="practitioner-link"
                >
                  <PersonPhoto name={practitioner.name} photo={practitioner.photo} className="w-14 h-14" />
                  <span>
                    <span className="block eyebrow text-champagne mb-1">{t(data.practitionerNote)}</span>
                    <span className="block font-serif text-lg text-[#161514] group-hover:text-white transition-colors">{practitioner.name}</span>
                  </span>
                  <ArrowRight size={18} className="text-champagne ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
              )}

              <Link to="/contact" className="inline-flex items-center gap-2 mt-10 bg-[#161514] text-white px-7 py-3.5 text-sm hover:bg-champagne transition-all duration-300 w-fit">
                {t(ui.cta.contact)} <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SantePhysique;
